import { isChord, normalizeChord } from './parse';

const SHARPS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLATS = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
/** Grafia convencional dos tons ao transpor (maiores com bemol, exceto F#; menores com sustenido). */
const MAJOR_KEYS = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
const MINOR_KEYS = [
  'Cm',
  'C#m',
  'Dm',
  'Ebm',
  'Em',
  'Fm',
  'F#m',
  'Gm',
  'G#m',
  'Am',
  'Bbm',
  'Bm',
];

/** Os 24 tons do enum `song_original_key` (e do `KeyPicker`). */
export const SONG_KEYS = [
  'C',
  'C#',
  'D',
  'Eb',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'Bb',
  'B',
  'Cm',
  'C#m',
  'Dm',
  'Ebm',
  'Em',
  'Fm',
  'F#m',
  'Gm',
  'G#m',
  'Am',
  'Bbm',
  'Bm',
] as const;
export type SongKey = (typeof SONG_KEYS)[number];

const NOTE_INDEX: Record<string, number> = {
  C: 0,
  'B#': 0,
  'C#': 1,
  Db: 1,
  D: 2,
  'D#': 3,
  Eb: 3,
  E: 4,
  Fb: 4,
  'E#': 5,
  F: 5,
  'F#': 6,
  Gb: 6,
  G: 7,
  'G#': 8,
  Ab: 8,
  A: 9,
  'A#': 10,
  Bb: 10,
  B: 11,
  Cb: 11,
};

/** Tons naturais que usam bemóis (os com `b` no nome já são detectados pela grafia). */
const FLAT_NATURAL_KEYS = new Set(['F', 'Dm', 'Gm', 'Cm', 'Fm']);

const mod12 = (n: number) => ((n % 12) + 12) % 12;

const stripAccents = (value: string) =>
  value.normalize('NFD').replace(/\p{Diacritic}/gu, '');

const parseKey = (key: string) => {
  const match = stripAccents(key.trim()).match(/^([A-Ga-g][#b]?)(m|min)?$/);
  if (!match) return null;

  const root = match[1]![0]!.toUpperCase() + match[1]!.slice(1);
  const index = NOTE_INDEX[root];
  return index === undefined ? null : { index, minor: Boolean(match[2]) };
};

/**
 * Leva qualquer grafia de tom (`Ab`, `Db`, `A#`, `Ém`, `em`) para um dos 24 tons do enum;
 * `null` se não for um tom. Usado na migração e na importação.
 */
export const normalizeKey = (key: string): SongKey | null => {
  const parsed = parseKey(key);
  if (!parsed) return null;
  return SONG_KEYS[parsed.index + (parsed.minor ? 12 : 0)]!;
};

/** Os 12 tons (maiores ou menores) em ordem cromática a partir de C, na grafia convencional. */
export const chromaticKeys = (minor: boolean) => (minor ? MINOR_KEYS : MAJOR_KEYS);

/** O tom é menor (`Am`, `F#m`)? */
export const isMinorKey = (key: string) => parseKey(key)?.minor ?? false;

/** Tom com a quantidade de semitons aplicada, na grafia convencional. Sem passos, devolve como veio. */
export const transposeKey = (key: string, steps: number) => {
  const parsed = parseKey(key);
  if (!parsed || steps === 0) return key.trim();
  return (parsed.minor ? MINOR_KEYS : MAJOR_KEYS)[mod12(parsed.index + steps)]!;
};

const prefersFlats = (key: string) => {
  const name = stripAccents(key.trim());
  if (name.includes('#')) return false;
  if (/^[A-G]b/.test(name)) return true;
  return FLAT_NATURAL_KEYS.has(name);
};

const CHORD = /^([A-G][#b]?)(.*)$/;

/** Escala de grafia: pelo tom alvo; sem tom, pela própria grafia do acorde; senão sustenidos. */
const scaleFor = (chordRoot: string, targetKey?: string) => {
  if (targetKey) return prefersFlats(targetKey) ? FLATS : SHARPS;
  if (chordRoot.endsWith('b')) return FLATS;
  return SHARPS;
};

const transposeRoot = (root: string, steps: number, scale: string[]) =>
  scale[mod12(NOTE_INDEX[root]! + steps)]!;

/**
 * Transpõe um acorde (raiz e baixo, mantendo o sufixo). `targetKey` define se a grafia usa
 * sustenidos ou bemóis. Acorde irreconhecível volta como veio.
 */
export const transposeChord = (chord: string, steps: number, targetKey?: string) => {
  const trimmed = normalizeChord(chord);
  if (steps === 0) return trimmed;

  const [main = '', bass] = trimmed.split('/');
  const match = main.match(CHORD);
  if (!match || NOTE_INDEX[match[1]!] === undefined) return trimmed;

  const scale = scaleFor(match[1]!, targetKey);
  let result = transposeRoot(match[1]!, steps, scale) + match[2];

  if (bass !== undefined) {
    const bassMatch = bass.match(/^([A-G][#b]?)$/);
    result += bassMatch ? `/${transposeRoot(bassMatch[1]!, steps, scale)}` : `/${bass}`;
  }

  return result;
};

/**
 * Transpõe todos os acordes `[X]` e `|X|` do conteúdo. Só trechos bem formados são tocados,
 * então um colchete solto numa linha não impede a transposição do resto da música.
 */
export const transposeContent = (content: string, steps: number, targetKey?: string) => {
  if (steps === 0) return content;

  return content.replace(
    /\[([^[\]\n]+)\]|\|([^|\n]+)\|/g,
    (whole, bracket: string | undefined, pipe: string | undefined) => {
      const tokens = (bracket ?? pipe)!.trim().split(/\s+/);
      if (pipe !== undefined && !tokens.every(isChord)) return whole;

      const transposed = tokens
        .map((chord) => transposeChord(chord, steps, targetKey))
        .join(' ');

      return bracket !== undefined ? `[${transposed}]` : `|${transposed}|`;
    },
  );
};

/** Menor deslocamento (−5…+6) entre dois tons; só a raiz conta, então tons menores mantêm o `m`. */
export const keyOffset = (from: string, to: string) => {
  const a = parseKey(from);
  const b = parseKey(to);
  if (!a || !b) return 0;

  const diff = mod12(b.index - a.index);
  return diff > 6 ? diff - 12 : diff;
};

export type ShiftInput = {
  originalKey: string;
  /** Tom escolhido no repertório; ausente = tom original. */
  setlistKey?: string | null;
  transpose?: number;
  capo?: number | null;
};

/** `(tomDoRepertório − tomOriginal) + transposição − capo` */
export const displayShift = ({
  originalKey,
  setlistKey,
  transpose = 0,
  capo,
}: ShiftInput) =>
  keyOffset(originalKey, setlistKey || originalKey) + transpose - (capo ?? 0);

/** Grafia convencional do tom (`G#` maior → `Ab`, `C#` maior → `Db`); desconhecido volta como veio. */
export const conventionalKey = (key: string) => {
  const parsed = parseKey(key);
  if (!parsed) return key.trim();
  return (parsed.minor ? MINOR_KEYS : MAJOR_KEYS)[parsed.index]!;
};

/** Tom mostrado: tom do repertório (ou original) com a transposição aplicada, em grafia convencional. */
export const displayKey = ({ originalKey, setlistKey, transpose = 0 }: ShiftInput) =>
  conventionalKey(transposeKey(setlistKey || originalKey, transpose));

/** Tom das formas tocadas (tom exibido menos o capo). */
export const shapeKey = (input: ShiftInput) =>
  transposeKey(displayKey(input), -(input.capo ?? 0));

/** Conteúdo pronto para exibir/imprimir, com os acordes nas formas que se tocam. */
export const renderedContent = (content: string, input: ShiftInput) =>
  transposeContent(content, displayShift(input), shapeKey(input));

/** Transposição que leva o tom do repertório (ou original) ao tom alvo escolhido no `KeyPicker`. */
export const transposeToKey = (input: ShiftInput, target: string) =>
  keyOffset(input.setlistKey || input.originalKey, target);
